import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, ArrowRight, Briefcase, Users, Calendar, PlayCircle,
  Sparkles, Search, Megaphone, BarChart2, MessageCircle, Linkedin, Mail,
  Target, User as UserIcon, TrendingUp, Flame
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = { cherche: "Je cherche", propose: "Je propose", opportunite: "Opportunité" };
const TYPE_BADGES = {
  cherche: "cbm-badge-type-cherche",
  propose: "cbm-badge-type-propose",
  opportunite: "cbm-badge-type-opportunite",
};

function Section({ title, subtitle, action, children, testId }) {
  return (
    <section className="mb-7" data-testid={testId}>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="font-outfit font-bold text-white text-xl" style={{ letterSpacing: "-0.02em" }}>{title}</h2>
          {subtitle && <p className="text-cbm-muted text-sm mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ProgressionBlock({ data, onNavigate }) {
  const pct = data?.pct || 0;

  let message, cta, path;
  if (pct === 0) {
    message = "Complétez votre profil pour commencer à recevoir des opportunités.";
    cta = "Compléter mon profil";
    path = "/mon-profil";
  } else if (pct < 100) {
    message = "Votre profil est en cours. Ajoutez vos informations pour augmenter votre visibilité.";
    cta = "Continuer";
    path = "/mon-profil";
  } else {
    message = "Votre profil est complet. Vous pouvez maintenant maximiser vos opportunités.";
    cta = "Voir les opportunités";
    path = "/opportunites";
  }

  // Single next step
  let nextStep = null;
  if (!data?.has_profile) {
    nextStep = { label: "Compléter votre profil (nom, offre, secteur)", path: "/mon-profil" };
  } else if (!data?.has_contact) {
    nextStep = { label: "Ajouter un moyen de contact", path: "/mon-profil" };
  } else if (!data?.has_opp) {
    nextStep = { label: "Publier une demande", path: "/poster-annonce" };
  } else {
    // 100% — keep engagement with a secondary action
    nextStep = { label: "Contacter des membres", path: "/annuaire" };
  }

  const color = pct >= 100 ? "#22c55e" : pct >= 60 ? "#C9A227" : pct >= 30 ? "#eab308" : "#f97316";

  return (
    <Section title="Votre progression" testId="block-progression">
      <div className="cbm-card p-5" style={{ borderLeftWidth: "3px", borderLeftColor: color }}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex-1 min-w-[260px]">
            <p className="text-white text-sm font-medium mb-1" data-testid="progression-label">
              Profil complété à <span className="font-outfit font-bold" style={{ color }}>{pct}%</span>
            </p>
            <p className="text-cbm-secondary text-sm">{message}</p>
          </div>
          <button
            className="cbm-btn-primary text-sm flex-shrink-0"
            onClick={() => onNavigate(path)}
            data-testid="progression-cta"
          >
            {cta} <ArrowRight size={13} />
          </button>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(184,196,224,0.12)" }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}66` }}
            data-testid="progression-bar"
          />
        </div>
        {nextStep && (
          <button
            onClick={() => onNavigate(nextStep.path)}
            className="mt-3 flex items-center gap-2 text-sm font-medium hover:underline"
            style={{ color: "#C9A227" }}
            data-testid="progression-next-step"
          >
            <CheckCircle2 size={14} />
            Prochaine étape : {nextStep.label}
            <ArrowRight size={13} />
          </button>
        )}
      </div>
    </Section>
  );
}

function TodoBlock({ progression, myStats, onNavigate }) {
  const todos = [];
  if (!progression?.has_profile || !progression?.has_contact) {
    todos.push({ icon: UserIcon, label: "Compléter mon profil", path: "/mon-profil" });
  }
  if (!progression?.has_opp) {
    todos.push({ icon: Megaphone, label: "Publier une demande", path: "/poster-annonce" });
  }
  if (myStats && myStats.cherche_total > 0 && myStats.cherche_resolved === 0) {
    todos.push({ icon: Target, label: "Marquer une demande comme trouvée", path: "/mon-profil" });
  }
  // fallback / addons — more concrete & measurable
  if (todos.length < 3) {
    todos.push({ icon: Users, label: "Contacter 3 membres", path: "/annuaire" });
  }
  if (todos.length < 3) {
    todos.push({ icon: Briefcase, label: "Répondre à 1 opportunité", path: "/opportunites" });
  }
  const list = todos.slice(0, 3);

  return (
    <Section title="À faire cette semaine" testId="block-todo">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {list.map((t, i) => {
          const Icon = t.icon;
          return (
            <button
              key={i}
              onClick={() => onNavigate(t.path)}
              className="cbm-card p-4 text-left hover:border-gold/50 transition-all flex items-center gap-3"
              data-testid={`todo-${i}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(201,162,39,0.14)", border: "1px solid rgba(201,162,39,0.3)" }}
              >
                <Icon size={16} className="text-gold" />
              </div>
              <span className="text-white text-sm font-medium flex-1">{t.label}</span>
              <ArrowRight size={14} className="text-cbm-muted" />
            </button>
          );
        })}
      </div>
    </Section>
  );
}

function WeeklyBlock({ weekly, onNavigate }) {
  const rows = [
    { key: "new_opportunities", icon: Briefcase, label: "Nouvelles opportunités", path: "/opportunites" },
    { key: "new_members", icon: Users, label: "Nouveaux membres", path: "/annuaire" },
    { key: "new_events", icon: Calendar, label: "Nouveaux événements", path: "/evenements" },
    { key: "new_replays", icon: PlayCircle, label: "Nouveaux replays", path: "/evenements" },
  ];

  return (
    <Section
      title="Activité récente du club"
      subtitle="7 derniers jours"
      testId="block-weekly"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map((r) => {
          const Icon = r.icon;
          const count = weekly?.[r.key] || 0;
          return (
            <button
              key={r.key}
              onClick={() => onNavigate(r.path)}
              className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              data-testid={`weekly-${r.key}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className="text-gold" />
                <p className="cbm-label">{r.label}</p>
              </div>
              <p className="font-outfit font-bold text-white" style={{ fontSize: "1.75rem", letterSpacing: "-0.02em", lineHeight: 1 }}>
                {count}
              </p>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

function OppsBlock({ opps, onNavigate }) {
  const shown = (opps || []).slice(0, 3);
  const subtitle = shown.length > 0
    ? `${shown.length} opportunité${shown.length > 1 ? "s" : ""} à saisir cette semaine`
    : "Opportunités récentes à saisir";

  if (shown.length === 0) {
    return (
      <Section
        title="Opportunités pour vous"
        subtitle={subtitle}
        testId="block-opps"
      >
        <div className="cbm-card p-5 text-center">
          <Sparkles size={22} className="text-cbm-muted mx-auto mb-2" />
          <p className="text-cbm-secondary text-sm">Aucune opportunité pour le moment.</p>
          <button className="cbm-btn-ghost text-sm mt-3" onClick={() => onNavigate("/opportunites")} data-testid="opps-empty-cta">
            Voir toutes les opportunités <ArrowRight size={13} />
          </button>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Opportunités pour vous"
      subtitle={subtitle}
      action={
        <button
          className="cbm-btn-ghost text-sm"
          onClick={() => onNavigate("/opportunites")}
          data-testid="opps-see-all"
        >
          Voir tout <ArrowRight size={13} />
        </button>
      }
      testId="block-opps"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((o) => (
          <div key={o._id} className="cbm-card p-4 flex flex-col" data-testid={`opp-${o._id}`}>
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <span className={`cbm-badge ${TYPE_BADGES[o.type] || ""}`}>
                {TYPE_LABELS[o.type] || o.type}
              </span>
              {o.secteur && <span className="cbm-badge" style={{ background: "rgba(155,169,202,0.12)", color: "#B8C4E0" }}>{o.secteur}</span>}
            </div>
            <p className="text-white text-sm font-semibold line-clamp-2 mb-1">{o.titre}</p>
            <p className="text-cbm-muted text-xs line-clamp-2 mb-3 flex-1">{o.description}</p>
            <button
              onClick={() => onNavigate(`/opportunites?q=${o._id}`)}
              className="cbm-btn-secondary text-xs w-full"
              data-testid={`opp-view-${o._id}`}
            >
              Voir l'opportunité <ArrowRight size={12} />
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function MembersBlock({ members, onNavigate }) {
  if (!members || members.length === 0) return null;

  const initialsOf = (p) => (p.nom_prenom || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const contact = (p) => {
    axios.post(`${API}/api/profiles/${p._id}/contact-click`, {}, { withCredentials: true }).catch(() => {});
    if (p.whatsapp) { window.open(`https://wa.me/${p.whatsapp.replace(/\D/g, "")}`, "_blank"); }
    else if (p.linkedin_url) { window.open(p.linkedin_url, "_blank"); }
    else if (p.email_visible && p.email) { window.open(`mailto:${p.email}`); }
  };

  return (
    <Section
      title="Membres à contacter"
      subtitle="Voici des membres avec qui vous pourriez collaborer"
      action={
        <button
          className="cbm-btn-ghost text-sm"
          onClick={() => onNavigate("/annuaire")}
          data-testid="members-see-all"
        >
          Voir l'annuaire <ArrowRight size={13} />
        </button>
      }
      testId="block-members"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {members.slice(0, 3).map((m) => {
          const photo = m.photo ? `${API}${m.photo}` : null;
          const hasContact = m.whatsapp || m.linkedin_url || (m.email_visible && m.email);
          const icon = m.whatsapp ? <MessageCircle size={13} /> : m.linkedin_url ? <Linkedin size={13} /> : <Mail size={13} />;
          return (
            <div key={m._id} className="cbm-card p-4 flex flex-col items-center text-center" data-testid={`member-${m._id}`}>
              {photo ? (
                <img src={photo} alt={m.nom_prenom} className="w-16 h-16 rounded-full object-cover mb-2 cbm-avatar" />
              ) : (
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-2 cbm-avatar text-gold font-outfit font-bold"
                  style={{ background: "linear-gradient(135deg, #132040, #0D1B3E)" }}
                >
                  {initialsOf(m)}
                </div>
              )}
              <p className="text-white text-sm font-semibold">{m.nom_prenom}</p>
              {m.fonction && <p className="text-xs font-semibold mt-0.5 line-clamp-1" style={{ color: "#C9A227" }} data-testid={`member-fonction-${m._id}`}>{m.fonction}</p>}
              {(m.is_ambassador || m.is_top_connector) && (
                <div className="flex items-center gap-1 flex-wrap justify-center mt-1.5">
                  {m.is_ambassador && <span className="cbm-badge cbm-badge-ambassador" data-testid={`dash-badge-ambassador-${m._id}`}><Sparkles size={8} />Ambassadeur</span>}
                  {m.is_top_connector && <span className="cbm-badge cbm-badge-top-connector" data-testid={`dash-badge-top-connector-${m._id}`}><Flame size={8} />Top connecteur</span>}
                </div>
              )}
              {m.entreprise && <p className="text-cbm-muted text-xs mt-0.5 line-clamp-1">{m.entreprise}</p>}
              {m.secteur && <p className="text-cbm-secondary text-xs mt-0.5 line-clamp-1">{m.secteur}</p>}
              <div className="flex items-center gap-1.5 mt-2" data-testid={`member-status-${m._id}`}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#22c55e", boxShadow: "0 0 6px rgba(34,197,94,0.6)" }} />
                <span className="text-xs font-medium" style={{ color: "#22c55e" }}>Disponible pour échanger</span>
              </div>
              <div className="flex gap-2 mt-3 w-full">
                <button
                  onClick={() => onNavigate(`/profil/${m._id}`)}
                  className="cbm-btn-ghost text-xs flex-1 border border-white/10"
                  data-testid={`member-view-${m._id}`}
                >
                  Voir
                </button>
                {hasContact && (
                  <button
                    onClick={() => contact(m)}
                    className="cbm-btn-secondary text-xs flex-1"
                    data-testid={`member-contact-${m._id}`}
                  >
                    {icon} Contacter
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function MyActivityBlock({ stats, onNavigate }) {
  if (!stats) return null;
  return (
    <Section
      title="Mon activité"
      action={
        <button
          className="cbm-btn-ghost text-sm"
          onClick={() => onNavigate("/mes-statistiques")}
          data-testid="my-activity-cta"
        >
          Voir mes statistiques <ArrowRight size={13} />
        </button>
      }
      testId="block-my-activity"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="cbm-card p-4" data-testid="activity-score">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-gold" />
            <p className="cbm-label">Score d'activité</p>
          </div>
          <p className="font-outfit font-bold text-white" style={{ fontSize: "1.75rem", lineHeight: 1 }}>
            {stats.activity_score}<span className="text-cbm-muted text-sm font-medium">/100</span>
          </p>
          <p className="text-cbm-secondary text-xs mt-1">{stats.score_label}</p>
        </div>
        <div className="cbm-card p-4" data-testid="activity-opps">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone size={14} className="text-gold" />
            <p className="cbm-label">Annonces publiées</p>
          </div>
          <p className="font-outfit font-bold text-white" style={{ fontSize: "1.75rem", lineHeight: 1 }}>
            {stats.opps_total}
          </p>
        </div>
        <div className="cbm-card p-4" data-testid="activity-resolution">
          <div className="flex items-center gap-2 mb-2">
            <BarChart2 size={14} className="text-gold" />
            <p className="cbm-label">Taux de résolution</p>
          </div>
          <p className="font-outfit font-bold text-white" style={{ fontSize: "1.75rem", lineHeight: 1 }}>
            {stats.cherche_total > 0 ? stats.resolution_rate : 0}<span className="text-cbm-muted text-sm font-medium">%</span>
          </p>
        </div>
      </div>
    </Section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/api/dashboard`, { withCredentials: true }),
      axios.get(`${API}/api/my-stats`, { withCredentials: true }),
    ])
      .then(([d, s]) => {
        if (!mounted) return;
        setDash(d.data);
        setMyStats(s.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="cbm-skeleton h-8 w-60 mb-6 rounded-lg" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="cbm-skeleton rounded-xl" style={{ height: 140 }} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="cbm-section-title">Tableau de bord</h1>
        <p className="text-cbm-muted text-sm mt-1">Votre espace d'action au sein du club</p>
      </div>

      <ProgressionBlock data={dash?.progression} onNavigate={navigate} />
      <TodoBlock progression={dash?.progression} myStats={myStats} onNavigate={navigate} />
      <WeeklyBlock weekly={dash?.weekly} onNavigate={navigate} />
      <OppsBlock opps={dash?.matching_opportunities} onNavigate={navigate} />
      <MembersBlock members={dash?.suggested_members} onNavigate={navigate} />
      <MyActivityBlock stats={myStats} onNavigate={navigate} />
    </div>
  );
}
